import { useState } from 'react';
import { SITE_URL } from '@/constants';

interface FAQ {
  question: string;
  answer: string;
}

const faqs: FAQ[] = [
  {
    question: 'What is a Sugargoo Spreadsheet?',
    answer: 'A Sugargoo spreadsheet is a curated, organized document that contains direct product links to items available through the Sugargoo shopping platform. These spreadsheets categorize products by type, brand, and seller, making it much easier to browse through thousands of available items without having to manually search. Our platform watches and tracks the best spreadsheets to keep you updated with the latest finds.',
  },
  {
    question: 'How often is the Sugargoo Spreadsheet updated?',
    answer: 'We monitor and update our featured Sugargoo spreadsheets on a daily basis. New products are added as they become available from trusted sellers, and outdated or dead links are regularly removed. The 2026 edition is refreshed every week to ensure you always have access to the most current and active product listings.',
  },
  {
    question: 'Is it safe to buy from Sugargoo using these spreadsheets?',
    answer: 'Yes, shopping through Sugargoo using verified spreadsheet links is safe when you follow basic precautions. Sugargoo provides buyer protection, QC (Quality Check) photos before shipping, and an agent verification system. We recommend always checking seller ratings, reviewing QC photos carefully, and using the links provided in trusted spreadsheets rather than random listings.',
  },
  {
    question: 'How do I use the QC photos feature on Sugargoo?',
    answer: 'After placing an order through Sugargoo, the platform provides QC (Quality Check) photos once your item arrives at their warehouse. These photos show the actual product you will receive. You should carefully inspect these photos for any defects, color discrepancies, or sizing issues before approving shipment. If the item does not match the description, you can request a return or exchange at this stage.',
  },
  {
    question: 'What categories are covered in the spreadsheet?',
    answer: 'Our featured Sugargoo spreadsheet covers 14 major categories including Shoes, Hoodies/Sweaters, T-Shirts, Jackets, Pants/Shorts, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and Other Stuff. Each category contains dozens of verified product links from trusted sellers, organized for easy browsing.',
  },
  {
    question: 'Do I need an account to use the Sugargoo Spreadsheet?',
    answer: 'You can browse the spreadsheet and view product links without an account. However, to actually purchase items through Sugargoo, you will need to create a free Sugargoo account to access their shopping platform, place orders, and use features like QC photos and international shipping.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="section-container">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-xs md:text-sm font-medium uppercase tracking-widest text-primary-600 mb-3">
            Got Questions?
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
            Frequently Asked Questions
          </h2>
        </div>

        {/* FAQ List */}
        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              className="bg-background-100 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => toggle(idx)}
                className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-background-200/50 transition-colors"
              >
                <h3 className="text-sm md:text-base font-semibold text-foreground-900 pr-4">
                  {faq.question}
                </h3>
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i
                    className={`ri-${openIndex === idx ? 'subtract' : 'add'}-line text-lg text-foreground-500 transition-transform duration-300 ${
                      openIndex === idx ? 'rotate-180' : ''
                    }`}
                  ></i>
                </div>
              </button>

              {openIndex === idx && (
                <div className="px-6 pb-5">
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Schema.org FAQ structured data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              '@id': `${SITE_URL}/#faq`,
              mainEntity: faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.answer,
                },
              })),
            }),
          }}
        />
      </div>
    </section>
  );
}